export const isFunction = (functionToCheck: any) => {
  return (
    functionToCheck && {}.toString.call(functionToCheck) === "[object Function]"
  );
};

export const convertTo24Hour = (time12h: string): string => {
  const [time, modifier] = time12h.split(" ");
  if (!time || !modifier) return "00:00";

  let [hours, minutes] = time.split(":");
  if (!hours || !minutes) return "00:00";

  if (hours === "12") {
    hours = "00";
  }

  if (modifier.toUpperCase() === "PM") {
    hours = (parseInt(hours, 10) + 12).toString();
  }

  return `${hours.padStart(2, "0")}:${minutes.padStart(2, "0")}`;
};

export const convertTo12Hour = (time24: string): string => {
  const [hoursStr, minutesStr] = time24.split(":");
  const hours = parseInt(hoursStr ?? "0", 10);
  const minutes = parseInt(minutesStr ?? "0", 10);

  const ampm = hours >= 12 ? "PM" : "AM";
  const hours12 = hours % 12 || 12;

  return `${hours12.toString().padStart(2, "0")}:${minutes
    .toString()
    .padStart(2, "0")} ${ampm}`;
};

export const isValid12HourTime = (timeString: string): boolean => {
  const regex = /^(0[1-9]|1[0-2]):([0-5][0-9])(?:\s?(AM|PM))?$/i;
  return regex.test(timeString);
};

// Utility to normalize memory values to bytes
export const UNIT_MULTIPLIERS: Record<string, number> = {
  B: 1,
  KB: 1024,
  MB: 1024 * 1024,
  GB: 1024 * 1024 * 1024,
};

// Parses a storage value string and converts it into a normalized value in bytes along with its unit.
export function normalizeStorageValue(value: string): {
  normalizedValue: number;
  unit: string;
  value: number;
} {
  const match = value.match(/^(\d+(?:\.\d+)?)\s*(B|KB|MB|GB)$/i);
  if (!match) {
    throw new Error(`Invalid storage value format: ${value}`);
  }

  const [, numericPart, unit] = match;
  // Ensure numericPart and unit are defined
  if (!numericPart || !unit) {
    throw new Error(`Invalid storage value format: ${value}`);
  }

  const normalizedValue =
    parseFloat(numericPart) * (UNIT_MULTIPLIERS[unit.toUpperCase()] || 1);

  return {
    normalizedValue,
    unit: unit.toUpperCase(),
    value: parseFloat(numericPart),
  };
}

// Converts a byte value into a readable string with the largest appropriate unit (GB, MB, KB, B).
export function formatMemoryValue(valueInBytes: number): string {
  const units = ["GB", "MB", "KB", "B"];

  for (const unit of units) {
    const factor = UNIT_MULTIPLIERS[unit];
    if (factor === undefined) {
      throw new Error(`Missing factor for unit: ${unit}`);
    }

    if (valueInBytes >= factor || unit === "B") {
      const normalizedValue = valueInBytes / factor;
      const formattedValue =
        normalizedValue % 1 === 0
          ? `${normalizedValue.toFixed(0)}`
          : `${normalizedValue.toFixed(2)}`;
      return `${formattedValue} ${unit}`;
    }
  }
  return `${valueInBytes} B`;
}

// convert date like yyyy-mm-dd hh:mm:ss IST to regular
export const formatDateISTRegular = (dateString: string): string => {
  const date = new Date(dateString);
  const day = date.getDate();
  const month = date.toLocaleString("en-IN", { month: "short" });
  const year = date.getFullYear();
  const hour = date.getHours();
  const minute = date.getMinutes();
  const ampm = hour >= 12 ? "PM" : "AM";

  const hour12 = hour % 12 || 12;
  const formattedMinute = minute < 10 ? `0${minute}` : minute;
  const formattedDate = `${day} ${month} ${year} ${hour12}:${formattedMinute} ${ampm}`;

  return formattedDate;
};

export const replaceSpaceWithUnderScore = (value: string): string => {
  return value.trim().replace(/\s+/g, "_");
};

export function secondsToHHMMSS(totalSeconds: number) {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  return [
    hours.toString().padStart(2, "0"),
    minutes.toString().padStart(2, "0"),
    seconds.toString().padStart(2, "0"),
  ].join(":");
}

export function downloadICSFile(
  icsContent: string,
  fileName: string = "event.ics",
) {
  const blob = new Blob([icsContent], { type: "text/calendar" });
  const url = URL.createObjectURL(blob);

  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  link.click();

  URL.revokeObjectURL(url);
}

export function generateICS(
  title: string,
  description: string,
  location: string,
  startDate: Date,
  endDate: Date,
) {
  const pad = (num: number) => (num < 10 ? `0${num}` : num);

  const formatDate = (date: Date) => {
    return (
      date.getUTCFullYear().toString() +
      pad(date.getUTCMonth() + 1) +
      pad(date.getUTCDate()) +
      "T" +
      pad(date.getUTCHours()) +
      pad(date.getUTCMinutes()) +
      pad(date.getUTCSeconds()) +
      "Z"
    );
  };

  const icsContent = `BEGIN:VCALENDAR

VERSION:2.0

BEGIN:VEVENT

SUMMARY:${title}

DESCRIPTION:${description}

LOCATION:${location}

DTSTART:${formatDate(startDate)}

DTEND:${formatDate(endDate)}

END:VEVENT

END:VCALENDAR`;

  return icsContent;
}

export const convertUrlsToFiles = async (
  urls: string[],
  folderPath: string = "/images/",
): Promise<File[]> => {
  const files = await Promise.all(
    urls.map(async (url) => {
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 8000);
        const response = await fetch(url, { signal: controller.signal });
        clearTimeout(timeoutId);
        if (!response.ok) return null;
        const blob = await response.blob();

        let filename = url.includes(folderPath)
          ? url.split(folderPath).pop()
          : url.split("/").pop();

        filename = filename || "file";

        const type = blob.type || "application/octet-stream";
        return new File([blob], filename, { type });
      } catch (e) {
        console.error("Failed to convert image URL to file", url, e);
        return null;
      }
    }),
  );

  return files.filter((file): file is File => file !== null);
};

export const formatTimeFromNumber = (seconds: number) => {
  if (!seconds) {
    return "--";
  }
  const h = String(Math.floor(seconds / 3600)).padStart(2, "0");
  const m = String(Math.floor((seconds % 3600) / 60)).padStart(2, "0");
  const s = String(seconds % 60).padStart(2, "0");
  return `${h}:${m}:${s}`;
};

export function detectImageMimeType(base64: string): string | null {
  const signatures: Record<string, string> = {
    "/9j/": "image/jpeg", // JPG
    iVBORw0KGgo: "image/png", // PNG
    R0lGODdh: "image/gif", // GIF
    R0lGODlh: "image/gif", // GIF
    UklGR: "image/webp", // WEBP
    Qk02: "image/bmp", // BMP
    PHN2Zy: "image/svg+xml", // SVG
  };

  for (const signature in signatures) {
    if (base64.startsWith(signature)) {
      return signatures[signature];
    }
  }

  return null; // unknown format
}

export const getDynamicPassage = (
  questionDescription: string,
  rawBlanks: any[],
  blanksAnswers: Record<string, string[]>,
) => {
  const desc = questionDescription || "";
  if (!Array.isArray(rawBlanks)) {
    return desc.replaceAll(
      "${blank}",
      "<u>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</u>",
    );
  }

  const parts = desc.split("${blank}");
  let finalHtml = "";

  parts.forEach((part, idx) => {
    finalHtml += part;
    if (idx < parts.length - 1) {
      const blankData = rawBlanks[idx];
      if (blankData) {
        const selectedKeys = blanksAnswers[blankData.blankId] || [];
        const selectedText = selectedKeys
          .map((k) => blankData.options?.[k])
          .filter(Boolean)
          .join(", ");

        if (selectedText) {
          finalHtml += `<u><strong><span style="color: var(--quiz-brand-color, #0ea5e9);font-size:14px;font-weight:700">&nbsp;${selectedText}&nbsp;</span></strong></u>`;
        } else {
          finalHtml += `<u>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</u>`;
        }
      } else {
        finalHtml += `<u>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</u>`;
      }
    }
  });

  return finalHtml;
};

export const getQuestionImageBlob = async (
  questionImage: string,
  timeoutMs = 8000,
): Promise<Blob | null> => {
  if (!questionImage) return null;
  if (questionImage.startsWith("data:") || !questionImage.includes("/")) {
    try {
      let base64Str = questionImage;
      if (!base64Str.startsWith("data:")) {
        if (base64Str.startsWith("iVBORw0KGgo")) {
          base64Str = `data:image/png;base64,${base64Str}`;
        } else if (base64Str.startsWith("/9j/")) {
          base64Str = `data:image/jpeg;base64,${base64Str}`;
        } else if (base64Str.startsWith("R0lGOD")) {
          base64Str = `data:image/gif;base64,${base64Str}`;
        } else {
          base64Str = `data:image/png;base64,${base64Str}`;
        }
      }
      const parts = base64Str.split(";base64,");
      const contentType =
        parts.length > 1 ? parts[0].replace("data:", "") : "image/png";
      const rawBase64 = parts.length > 1 ? parts[1] : parts[0];
      const byteCharacters = atob(rawBase64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      return new Blob([byteArray], { type: contentType });
    } catch (e) {
      console.error("Failed to parse base64 image", e);
      return null;
    }
  } else {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
    try {
      const response = await fetch(questionImage, { signal: controller.signal });
      if (!response.ok) return null;
      return await response.blob();
    } catch (e) {
      console.error("Failed to fetch image URL", e);
      return null;
    } finally {
      clearTimeout(timeoutId);
    }
  }
};

/**
 * Normalizes snippet output and answer strings by:
 * 1. Converting Windows \r\n and \r line endings to Unix \n
 * 2. Expanding tab characters \t to 4 spaces
 * 3. Removing non-printable control characters (\0 null, \b backspace, \v vertical tab, \f form feed)
 * 4. Stripping trailing newlines
 */
export const formatSnippetAnswer = (val: unknown): string => {
  if (val === null || val === undefined) return "";
  const str = typeof val === "string" ? val : String(val);
  return str
    .replace(/\r\n/g, "\n")
    .replace(/\r/g, "\n")
    .replace(/\t/g, "    ")
    .replace(/[\u0000\u0008\u000B\u000C]/g, "")
    .replace(/\n+$/, "");
};

export const isNetworkOrServerError = (error: unknown): boolean => {
  if (!error) return false;

  if (typeof navigator !== "undefined" && !navigator.onLine) {
    return true;
  }

  const err = error as {
    status?: string | number;
    originalStatus?: number;
    statusCode?: number;
    data?: {
      status?: number;
      statusCode?: number;
      isNetworkIssue?: boolean;
      message?: string;
      response?: string;
    };
    error?: string;
    message?: string;
    name?: string;
    code?: string;
  };

  if (err?.data?.isNetworkIssue) {
    return true;
  }

  if (err?.status === "FETCH_ERROR" || err?.status === "TIMEOUT_ERROR") {
    return true;
  }

  const is5xxCode = (code: unknown): boolean => {
    const num = typeof code === "number" ? code : parseInt(String(code), 10);
    return !isNaN(num) && num >= 500 && num <= 599;
  };

  if (
    is5xxCode(err?.status) ||
    is5xxCode(err?.originalStatus) ||
    is5xxCode(err?.statusCode) ||
    is5xxCode(err?.data?.status) ||
    is5xxCode(err?.data?.statusCode)
  ) {
    return true;
  }

  if (
    err?.code === "ERR_NETWORK" ||
    err?.code === "ECONNABORTED" ||
    err?.code === "ENOTFOUND" ||
    err?.code === "ECONNREFUSED"
  ) {
    return true;
  }

  const errorString = (
    typeof error === "string"
      ? error
      : `${err?.error || ""} ${err?.message || ""} ${err?.name || ""} ${err?.data?.message || ""}`
  ).toLowerCase();

  if (
    errorString.includes("failed to fetch") ||
    errorString.includes("network") ||
    errorString.includes("timeout") ||
    errorString.includes("offline") ||
    errorString.includes("load failed") ||
    errorString.includes("connection")
  ) {
    return true;
  }

  return false;
};

